import { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import colors from 'styles/colors';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from 'components/system/Input';
import useSubmitProblemSolution from 'hooks/useSubmitProblemSolution';
import ProblemCard from './ProblemCard';

const SubmissionForm = ({ competitionProblems, competitionSubmissions, competitionId }) => {
  const { mutate } = useSubmitProblemSolution();
  const [submissions, setSubmissions] = useState(competitionSubmissions);

  const formFields = competitionProblems?.map((problem) => ([{
    name: problem.problemId,
    id: problem.problemId.toLowerCase(),
    points: problem.points,
    order: problem.order,
    grid: 12,
    component: Input,
    validationMessage: problem.answerValidationErrorMessage || 'Invalid format',
    validationPattern: problem.answerValidationRegexp,
  },
  {
    name: `${problem.problemId}_description`,
    grid: 12,
    component: Input,
    inputType: 'textarea',
  }])) || [];

  const createValidationSchema = () => {
    const shape = {};
    formFields.forEach((fieldGroup) => {
      fieldGroup.forEach((field) => {
        if (field.name.endsWith('_description')) {
          shape[field.name] = Yup.string()
            .required('This field is required')
            .max(800, 'Description cannot exceed 800 characters');
        } else if (field.validationPattern) {
          shape[field.name] = Yup.string()
            .matches(new RegExp(field.validationPattern), field.validationMessage)
            .required('This field is required');
        }
      });
    });
    return Yup.object().shape(shape);
  };

  const formik = useFormik({
    initialValues: formFields.reduce((values, fieldGroup) => {
      const newValues = { ...values };
      fieldGroup.forEach((field) => {
        newValues[field.name] = '';
      });
      return newValues;
    }, {}),
    validationSchema: createValidationSchema(),
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const handleBlurValidate = async (fieldName) => {
    await formik.validateField(fieldName);
    formik.setFieldTouched(fieldName, true);
  };

  const handleFieldSubmit = async (fieldName, textareaName) => {
    await formik.validateField(fieldName);
    await formik.validateField(textareaName);
    formik.setFieldTouched(fieldName, true);
    formik.setFieldTouched(textareaName, true);

    if (formik.errors[fieldName] || formik.errors[textareaName]) {
      console.error('❌ Validation failed', fieldName ? formik.errors[fieldName] : '');
      console.error('❌ Validation failed', textareaName ? formik.errors[textareaName] : '');
    } else {
      const submissionId = fieldName.toLowerCase();
      const answer = formik.values[fieldName];
      const description = formik.values[textareaName];
      try {
        const response = await mutate({
          competitionId, problemId: fieldName, answer, description,
        });
        console.log(`✅ Validated: { ${fieldName}: ${answer} }`);
        console.log(`✅ Validated: { ${textareaName}: ${description} }`);
        console.log('✅ Success');

        // Clear input values after successful submission
        formik.setFieldValue(fieldName, '');
        formik.setFieldTouched(fieldName, false);
        formik.setFieldValue(textareaName, '');
        formik.setFieldTouched(textareaName, false);

        setSubmissions((prevSubmissions) => ({
          ...prevSubmissions,
          [submissionId]: {
            ...prevSubmissions[submissionId],
            submissions: [response, ...(prevSubmissions[submissionId]?.submissions || [])],
          },
        }));
      } catch (error) {
        console.error(`❌ Submission failed for ${fieldName}:`, error);
        formik.setFieldError(textareaName, 'Failed to submit answer, please try again later.');
      }
    }
  };

  useEffect(() => {
    setSubmissions(competitionSubmissions);
  }, [competitionSubmissions]);

  return (
    <Grid
      container
      justifyContent="center"
      sx={{
        background: colors.background.paper_5p,
        border: `1px solid ${colors.border.border_5p}`,
        borderRadius: '8px',
        padding: '24px',
      }}
    >
      {formFields.map((fieldGroup) => {
        const problemField = fieldGroup[0]; // First item contains problem details
        const descriptionField = fieldGroup[1]; // Second item is description field

        return (
          <ProblemCard
            key={problemField.id}
            config={problemField}
            descriptionConfig={descriptionField}
            problemName={submissions[problemField.id]?.problemName}
            submissions={submissions[problemField.id]?.submissions || []}
            formik={formik}
            handleBlurValidate={handleBlurValidate}
            handleFieldSubmit={handleFieldSubmit}
          />
        );
      })}
    </Grid>
  );
};

export default SubmissionForm;
